/**
 * Display rules for values that recur across pillar domains.
 *
 * Everything here is pure and takes its inputs explicitly — including the
 * clock. A formatter that reads `Date.now()` is a side effect during render,
 * and a relative age that changes between two renders of the same data is a
 * figure nobody can verify against the capture.
 */
import { MONEY_LOCALE } from "./cresultcard.money";

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const MILLISECONDS_PER_SECOND = 1000;
const MINUTE_PAD = 2;
const CLOCK_SEPARATOR = ":";
const CLOCK_ZERO = "0:00";
const PAD_CHARACTER = "0";

const ORDINAL_SUFFIX_DEFAULT = "th";
const ORDINAL_SUFFIX_BY_ONES: Readonly<Record<number, string>> = {
  1: "st",
  2: "nd",
  3: "rd",
};
const ORDINAL_TEEN_START = 11;
const ORDINAL_TEEN_END = 13;

const EMPTY_LENGTH = 0;
const WWW_PREFIX = "www.";
const BARE_HOST_PATTERN =
  /^(?:www\.)?(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

const AGE_JUST_NOW = "just now";
const AGE_MINUTE_SUFFIX = "m ago";
const AGE_HOUR_SUFFIX = "h ago";
const AGE_DAY_SUFFIX = "d ago";
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

/** A duration in seconds, as a clock. `1:04`, `12:07`, `1:02:03`. */
export function formatClockFromSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return CLOCK_ZERO;
  }
  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / SECONDS_PER_HOUR);
  const minutes = Math.floor((whole % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const remainder = whole % SECONDS_PER_MINUTE;
  const paddedSeconds = String(remainder).padStart(MINUTE_PAD, PAD_CHARACTER);
  if (hours === 0) {
    return `${String(minutes)}${CLOCK_SEPARATOR}${paddedSeconds}`;
  }
  const paddedMinutes = String(minutes).padStart(MINUTE_PAD, PAD_CHARACTER);
  return `${String(hours)}${CLOCK_SEPARATOR}${paddedMinutes}${CLOCK_SEPARATOR}${paddedSeconds}`;
}

/** The same clock, from the milliseconds a media element reports. */
export function formatClockFromMilliseconds(milliseconds: number): string {
  return formatClockFromSeconds(milliseconds / MILLISECONDS_PER_SECOND);
}

/** Thousands grouping for a plain count, on the same pinned locale as money. */
export function formatCount(value: number): string {
  return new Intl.NumberFormat(MONEY_LOCALE).format(value);
}

/** `1st`, `2nd`, `3rd`, `11th`. */
export function formatOrdinal(value: number): string {
  const tens = value % 100;
  const isTeen = tens >= ORDINAL_TEEN_START && tens <= ORDINAL_TEEN_END;
  const suffix = isTeen
    ? ORDINAL_SUFFIX_DEFAULT
    : (ORDINAL_SUFFIX_BY_ONES[value % 10] ?? ORDINAL_SUFFIX_DEFAULT);
  return `${String(value)}${suffix}`;
}

interface RelativeAgeInput {
  readonly capturedAtMs: number;
  /** Supplied by the caller, so the same props always render the same text. */
  readonly nowMs: number;
}

/** Freshness as a figure. There is no breathing dot anywhere in this kit. */
export function formatRelativeAge({
  capturedAtMs,
  nowMs,
}: RelativeAgeInput): string {
  const elapsedMinutes = Math.floor(
    (nowMs - capturedAtMs) / (MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE),
  );
  if (elapsedMinutes < 1) {
    return AGE_JUST_NOW;
  }
  if (elapsedMinutes < MINUTES_PER_HOUR) {
    return `${String(elapsedMinutes)}${AGE_MINUTE_SUFFIX}`;
  }
  const elapsedHours = Math.floor(elapsedMinutes / MINUTES_PER_HOUR);
  if (elapsedHours < HOURS_PER_DAY) {
    return `${String(elapsedHours)}${AGE_HOUR_SUFFIX}`;
  }
  return `${String(Math.floor(elapsedHours / HOURS_PER_DAY))}${AGE_DAY_SUFFIX}`;
}

interface PlaybackPositionInput {
  readonly elapsedSeconds: number;
  readonly totalSeconds: number;
}

/**
 * A scrubber's `aria-valuetext` in human form. Raw seconds read out as a
 * four-digit number, which is not a position anyone can act on.
 */
export function formatPlaybackPosition({
  elapsedSeconds,
  totalSeconds,
}: PlaybackPositionInput): string {
  return `${formatClockFromSeconds(elapsedSeconds)} of ${formatClockFromSeconds(totalSeconds)}`;
}

/**
 * The host a captured address points at — the word a control opens WITH, now
 * that a link never renders as plain text and its visible word is never a raw
 * url (ruled 2026-08-22).
 *
 * It is a SHORTER SPELLING of a value the caller already holds, read out with
 * the platform's own parser; it composes nothing, and the `href` stays the
 * captured address verbatim, so what the control opens is never what this
 * returns. A leading `www.` is dropped because it distinguishes no two hosts in
 * any population and reads as noise in a control.
 *
 * `null` where the address carries no host at all — a `mailto:`, a relative
 * form, anything `URL` refuses. The branch exists because an address arrives as
 * a `string`, and a string is not a promise of a hostname.
 *
 * Three families asked the same question with three private copies of these six
 * lines (web-search, scriptures, social). One question, one answer.
 */
export function crcAddressHost(address: string): string | null {
  try {
    const hostname = new URL(address).hostname;
    if (hostname.length === EMPTY_LENGTH) {
      return null;
    }
    return hostname.startsWith(WWW_PREFIX)
      ? hostname.slice(WWW_PREFIX.length)
      : hostname;
  } catch {
    return null;
  }
}

// A bare host occupies the complete value; URLs, paths, mailboxes and prose do not.
export function crcBareHost(value: string): string | null {
  return BARE_HOST_PATTERN.test(value) ? value : null;
}

// A leading www. distinguishes no publisher on these display surfaces.
export function crcBareHostDisplay(value: string): string | null {
  const host = crcBareHost(value);
  if (host == null) return null;
  return host.toLowerCase().startsWith(WWW_PREFIX)
    ? host.slice(WWW_PREFIX.length)
    : host;
}
